import {Formik, FormikHelpers, FormikProps} from 'formik';
import {forEach, map} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import {billingPlatformApi, prospectApi} from 'api';
import {Button, FormGroup, Loader, Form, EmailLink, PasswordInputFormik, Icon} from 'components';
import {APP_SUPPORT_EMAIL} from 'constants/app';
import {PARTNER_STACK_SCRIPT_PATH} from 'constants/billing-platform';
import {BLUE_AGENT_LOGO_PATH, COLORED_AGENT_LOGO_PATH} from 'constants/images';
import {ROUTE_PATH} from 'constants/routes';
import {history} from 'context/history';
import {AnalyticsOnboardingEventName, ValidationCode} from 'enums';
import {useApiData, useExternalScript} from 'hooks';
import {UnauthorizedLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale';
import {analyticsService, partnerStackService} from 'services';
import {ApiError} from 'types';
import {apiUtils} from 'utils';
import {dateUtils} from 'utils/date';
import {getParticipantName} from 'utils/transform';

const FORM_CONTROL_T_KEYS = TRANSLATION_KEYS.COMMON.FORM_CONTROL;
const T_KEYS = TRANSLATION_KEYS.CONFIRM_PROSPECT_PAGE;
const PASSWORD_ERRORS_T_KEYS = TRANSLATION_KEYS.COMMON.PASSWORD_ERRORS;
const PAGE_KEYS = TRANSLATION_KEYS.LOGIN_PAGE;

const OVERRIDES = {
    confirmPassword: {
        [ValidationCode.Invalid]: PASSWORD_ERRORS_T_KEYS.PASSWORD_THE_SAME,
    }
};

type FormFields = {
    password: string;
    confirmPassword: string;
};

type FormTemplateProps = {
    error: ApiError;
} & FormikProps<FormFields>;

enum ProspectState {
    Loading = 'loading',
    InvalidToken = 'invalid-token',
    Confirmation = 'confirmation',
    Confirmed = 'confirmed',
    UserExists = 'user-exists'
}

const FormTemplate = ({values, error, handleSubmit, setFieldError}: FormTemplateProps) => {
    const {t} = useTranslation();

    useEffect(() => {
        const err = apiUtils.getFieldsValidationErrorMessages(
            error, t, [{field: 'confirmPassword', overrides: OVERRIDES.confirmPassword}]
        );
        forEach(err, (value, key) => value && setFieldError(key, value));
    }, [setFieldError, t, error]);

    return (
        <Form
            noValidate
            className='tw-mt-2'
            onSubmit={handleSubmit}
        >
            <FormGroup>
                <PasswordInputFormik
                    required
                    fontLarge
                    name='password'
                    autoComplete='new-password'
                    label={t(FORM_CONTROL_T_KEYS.PASSWORD)}
                />
            </FormGroup>

            <FormGroup>
                <PasswordInputFormik
                    required
                    fontLarge
                    name='confirmPassword'
                    label={t(FORM_CONTROL_T_KEYS.CONFIRM_PASSWORD)}
                />
            </FormGroup>

            {map(error?.response?.data?.errors?.password?.expected, (errorMessage) => {
                const errorTKey = PASSWORD_ERRORS_T_KEYS[errorMessage.toUpperCase()] || PASSWORD_ERRORS_T_KEYS.DEFAULT;
                return (<div key={errorMessage} className='tw-text-alert'>{t(errorTKey)}</div>);
            })}

            <div className='tw-mt-12 tw-text-center'>
                <Button
                    type='submit'
                    width='large'
                    height='large'
                    color='primary'
                    fontSize='large'
                    fontStyle='secondary'
                    disabled={!values.password || !values.confirmPassword}
                    rounded
                >
                    {t(T_KEYS.FORM.BUTTON.CREATE_ACCOUNT)}
                </Button>
            </div>
        </Form>
    );
};

export const ConfirmProspectPage: React.FC = () => {
    const {t} = useTranslation();
    const [initialValues, setInitialValues] = useState<FormFields>({
        password: '',
        confirmPassword: ''
    });

    const [confirmed, setConfirmed] = useState(false);
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const {loaded: psScriptLoaded} = useExternalScript(PARTNER_STACK_SCRIPT_PATH);

    const token = useMemo(() => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('token');
    }, []);

    const goToLogin = useCallback(() => history.push(ROUTE_PATH.PUBLIC.LOGIN), []);

    const handleErrorProspectResponse = useCallback(() => setIsTokenValid(false), []);
    const handleSuccessProspectResponse = useCallback(() => setIsTokenValid(true), []);

    const {send: loadProspect, data: prospectInfo, loading} = useApiData(
        prospectApi.getProspect, {onSuccess: handleSuccessProspectResponse, onError: handleErrorProspectResponse}
    );

    const {send: getPSCredentials, data: psCredentials} = useApiData(billingPlatformApi.getPartnerStackCredentials);

    const onSuccessConfirm = useCallback(() => {
        setConfirmed(true);
        analyticsService.trackOnboardingEvent(AnalyticsOnboardingEventName.AccountCreated, {
            plan_id: prospectInfo?.prospect.planId
        });
    }, [prospectInfo]);

    const {send: confirmProspect, loading: confirmLoading, error: confirmError} = useApiData(
        prospectApi.confirmProspect, {onSuccess: onSuccessConfirm}
    );

    const psCustomerKey = useMemo(() => prospectInfo?.prospect.integrations?.chargebee?.customer_key, [prospectInfo]);

    const activeState = useMemo(() => {
        if (loading || confirmLoading) {
            return ProspectState.Loading;
        } else if (initialized && !isTokenValid) {
            return ProspectState.InvalidToken;
        } else if (prospectInfo?.userExists) {
            return ProspectState.UserExists;
        } else if (isTokenValid && !confirmed) {
            return ProspectState.Confirmation;
        } else if (confirmed) {
            return ProspectState.Confirmed;
        }
    }, [loading, confirmLoading, initialized, isTokenValid, confirmed, prospectInfo]);

    const imageParams = useMemo(() => activeState === ProspectState.Confirmed ?
        {src: COLORED_AGENT_LOGO_PATH, alt: t(T_KEYS.CREATION_COMPLETE_IMG)} :
        {src: BLUE_AGENT_LOGO_PATH, alt: t(T_KEYS.TITLE)},
    [activeState, t]);

    const title = useMemo(() => {
        if (activeState === ProspectState.UserExists) {
            return t(T_KEYS.USER_EXISTS.TITLE);
        } else if (activeState === ProspectState.Confirmed) {
            return t(T_KEYS.TITLE_COMPLETE);
        } else {
            return t(T_KEYS.TITLE);
        }
    }, [activeState, t]);

    const renderGoToLoginBtn = useCallback(() => (
        <div className='tw-mt-12 tw-text-center'>
            <Button
                type='submit'
                width='large'
                height='large'
                color='primary'
                fontSize='large'
                fontStyle='secondary'
                rounded
                onClick={goToLogin}
            >
                {t(T_KEYS.LOGIN_BTN)}
            </Button>
        </div>
    ), [goToLogin, t]);

    const handleSubmit = useCallback((values: FormFields, {setSubmitting}: FormikHelpers<FormFields>) => {
        setInitialValues(values);
        setSubmitting(true);

        const {password, confirmPassword} = values;
        const timeZone = dateUtils.guessTimeZone();
        confirmProspect({token, password, confirmPassword, timeZone});
    }, [confirmProspect, token]);

    useEffect(() => {
        if (!initialized && token) {
            loadProspect(token);
            setInitialized(true);
        }
    }, [initialized, loadProspect, token]);

    useEffect(() => {
        if (!token) {
            setInitialized(true);
            setIsTokenValid(false);
        }
    }, [token]);

    useEffect(() => {
        if (activeState === ProspectState.Confirmation && psCustomerKey && psScriptLoaded) {
            getPSCredentials();
        }
    }, [activeState, psScriptLoaded, psCustomerKey, getPSCredentials]);

    useEffect(() => {
        if (psCredentials?.publicKey) {
            partnerStackService.init(psCredentials.publicKey);
        }
    }, [psCredentials]);

    useEffect(() => {
        if (activeState === ProspectState.Confirmed && psCustomerKey && psCredentials?.publicKey) {
            partnerStackService.setUserInfo({
                name: prospectInfo.prospect.company,
                email: prospectInfo.prospect.email,
                customerKey: psCustomerKey
            });
            partnerStackService.signup();
        }
    }, [activeState, prospectInfo, psCredentials, psCustomerKey]);

    return (
        <UnauthorizedLayout>
            <div
                className='sm:tw-bg-light tw-px-16 tw-py-8 tw-flex tw-flex-col tw-justify-center
                    tw-text-dark sm:tw-shadow-lg sm:tw-w-160'
            >
                <div className='tw-text-center tw-text-3xl tw-font-secondary'>
                    {title}
                    {activeState === ProspectState.Confirmed && <div className='tw-text-center'>{t(T_KEYS.CREATION_COMPLETE)}</div>}
                </div>
                <div className='tw-px-18 tw-pb-8'>
                    {activeState === ProspectState.Loading &&
                        <div className='tw-relative tw-mt-6 tw-w-12 tw-h-12 tw-mx-auto'><Loader /></div>}
                    {activeState === ProspectState.InvalidToken &&
                        <div className='tw-pt-4 tw-ts-h4'>
                            {`${t(T_KEYS.INVALID_TOKEN)} `}
                            <EmailLink email={APP_SUPPORT_EMAIL} />
                        </div>}
                    {activeState === ProspectState.UserExists &&
                        <div className='tw-pt-4 tw-ts-h4 tw-flex tw-flex-col tw-items-center'>
                            <Icon name='x-circle' color='alert' size='8xl' className='tw-block tw-my-4 w-mx-auto' />
                            {t(T_KEYS.USER_EXISTS.MESSAGE)}
                            <div className='tw-mt-2 tw-font-bold'>{prospectInfo?.prospect.email}</div>
                            {renderGoToLoginBtn()}
                            <Link
                                to={ROUTE_PATH.PUBLIC.PASSWORD.FORGOT}
                                className='tw-block tw-text-center tw-mt-5 tw-text-primary tw-underline tw-whitespace-nowrap'
                            >
                                {t(PAGE_KEYS.FORGOT_PASSWORD)}
                            </Link>
                        </div>}
                    {activeState === ProspectState.Confirmation &&
                        <div>
                            <img {...imageParams} className='tw-mx-auto tw-my-12' />
                            <div className='tw-mt-1 sm:tw-mb-8 tw-text-dark tw-font-medium'>
                                <div className='tw-ts-h4-bold'>
                                    {`${t(T_KEYS.GREETING)} ${getParticipantName(prospectInfo?.prospect)},`}
                                </div>
                                <div className='tw-ts-h4'>{t(T_KEYS.PASSWORD_FOR)}</div>
                            </div>
                            <Formik
                                initialValues={initialValues}
                                validateOnChange
                                validateOnBlur={false}
                                onSubmit={handleSubmit}
                            >
                                {(props) => (
                                    <FormTemplate {...props} error={confirmError} />
                                )}
                            </Formik>
                        </div>}
                    {activeState === ProspectState.Confirmed &&
                        <div>
                            <img {...imageParams} className='tw-mx-auto tw-my-12' />
                            <div className='tw-mt-3 tw-ts-h4'>
                                <div>
                                    <span className='tw-ts-h4-bold'>{t(T_KEYS.ACCOUNT)}</span>
                                    <span>{` ${prospectInfo?.prospect.company}`}</span>
                                </div>
                                <div className='tw-mt-3'>{t(T_KEYS.THANKS)}</div>
                                <div>{t(T_KEYS.CREATION_COMPLETE_RESUME)}</div>
                                {renderGoToLoginBtn()}
                            </div>
                        </div>}
                </div>
            </div>
        </UnauthorizedLayout>
    );
};
